import "dayjs/locale/ja";
import { Alert, Button, Checkbox, Group, Select, TextInput } from "@mantine/core";
import { DatePickerInput, DatesRangeValue, MonthPickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconCalendar, IconInfoCircle } from "@tabler/icons-react";
import { lastDayOfMonth, startOfMonth, subMonths } from "date-fns";
import { useEffect } from "react";
import { match, P } from "ts-pattern";
import { useExecuteAnalysis } from "@/hooks/useExecuteAnalysis";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { CategoryTree } from "@/models/categoryTree";

type FormInputs = {
  dateRange: DatesRangeValue;
  period: Date | undefined;
  categoryTreeId: string;
  title: string;
  isPeriodicAnalysis: boolean;
};

interface Props {
  categoryTrees: CategoryTree[];
  isAnalysisRunning: boolean;
}

export const ExecuteAnalysisButton: React.FC<Props> = ({ categoryTrees, isAnalysisRunning }) => {
  const { executeAnalysis, isLoading } = useExecuteAnalysis();
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const startOfThisMonth = startOfMonth(new Date());
  const defaultPeriodStart = subMonths(startOfThisMonth, 1); // 前月
  const defaultPeriodEnd = lastDayOfMonth(defaultPeriodStart); // 今月の初日

  const form = useForm<FormInputs>({
    initialValues: {
      isPeriodicAnalysis: true,
      period: defaultPeriodStart,
      dateRange: [defaultPeriodStart, defaultPeriodEnd],
      categoryTreeId: categoryTrees?.[0].id ?? "",
      title: "",
    },
    validateInputOnChange: true,
    validate: {
      dateRange: (value, values) =>
        !values.isPeriodicAnalysis && (!value[0] || !value[1]) ? "日付範囲を選択してください。" : null,
      period: (value, values) => values.isPeriodicAnalysis && value === undefined ? "期間を選択してください。" : null,
      title: (value) =>
        match(value.length).with(0, () => "分析タイトルを入力してください。").with(
          P.when((v) => v > 100),
          () => "100文字以内で入力してください。",
        ).otherwise(() => null),
    },
    onValuesChange: (values, previous) => {
      // Only process if relevant fields changed
      if (values.isPeriodicAnalysis !== previous.isPeriodicAnalysis || values.period !== previous.period) {
        // Handle periodic analysis mode (true)
        if (values.isPeriodicAnalysis && values.period) {
          const startDate = startOfMonth(values.period);
          const endDate = lastDayOfMonth(values.period);

          // Only update if the current dateRange doesn't match what we want to set
          const currentStartIsDifferent = !values.dateRange?.[0]
            || startDate.toString() !== values.dateRange[0].toString();
          const currentEndIsDifferent = !values.dateRange?.[1]
            || endDate.toString() !== values.dateRange[1].toString();

          if (currentStartIsDifferent || currentEndIsDifferent) {
            form.setFieldValue("dateRange", [startDate, endDate]);
          }
        }

        // Handle non-periodic analysis mode (false)
        if (!values.isPeriodicAnalysis && values.period !== undefined) {
          form.setFieldValue("period", undefined);
        }
      }
    },
  });

  useEffect(() => form.reset, [sessionCurrentWorkspaceId]);

  const categoryOptions = categoryTrees?.map(ct => ({ value: ct.id, label: ct.version }));

  async function handleExecuteAnalysis() {
    const result = form.validate();
    if (result.hasErrors) return;

    const values = form.getValues();
    if (values.dateRange[0] === null || values.dateRange[1] === null) {
      form.setFieldError("dateRange", "日付範囲を選択してください。");
      return;
    }
    await executeAnalysis(
      values.title,
      values.dateRange[0], // startDate
      values.dateRange[1], // endDate
      values.categoryTreeId,
      values.period ?? undefined,
      values.isPeriodicAnalysis,
    );
  }

  const icon = <IconInfoCircle />;

  return (
    <form>
      {isAnalysisRunning && (
        <Alert variant="light" color="yellow" icon={icon} style={{ marginBottom: "1rem" }}>
          他の分析が進行中です。
        </Alert>
      )}

      <Group mt={3} align="start">
        <Checkbox
          data-testid="isPeriodicAnalysis"
          label="定期分析"
          checked={form.values.isPeriodicAnalysis}
          mb="sm"
          {...form.getInputProps("isPeriodicAnalysis", { type: "checkbox" })}
        />
      </Group>
      <Group mt={3} align="start">
        <MonthPickerInput
          data-testid="period"
          placeholder="対象年月"
          label="定期分析対象"
          valueFormat="YYYY-MM"
          leftSection={<IconCalendar size={18} stroke={1.5} />}
          leftSectionPointerEvents="none"
          value={form.values.period}
          error={form.errors.period}
          required={form.values.isPeriodicAnalysis}
          disabled={!form.values.isPeriodicAnalysis}
          style={{ width: "7rem" }}
          maxDate={new Date()}
          locale="ja"
          {...form.getInputProps("period")}
        />
        <Group gap="xs">
          <DatePickerInput
            data-testid="dateRange"
            placeholder="期間を選択"
            label="分析対象期間"
            type="range"
            allowSingleDateInRange={true}
            valueFormat="YYYY-MM-DD"
            leftSection={<IconCalendar size={18} stroke={1.5} />}
            leftSectionPointerEvents="none"
            value={form.values.dateRange}
            error={form.errors.dateRange && "日付範囲を選択してください"}
            required={!form.values.isPeriodicAnalysis}
            disabled={form.values.isPeriodicAnalysis}
            maxDate={new Date()}
            locale="ja"
            {...form.getInputProps("dateRange")}
            style={{ width: "15rem" }}
          />
        </Group>
        <Select
          data-testid="categoryVersion"
          placeholder="カテゴリバージョン"
          label="カテゴリバージョン"
          allowDeselect={false}
          maxDropdownHeight={200}
          style={{ width: "9rem" }}
          data={categoryOptions}
          {...form.getInputProps("categoryTreeId")}
        />
        <TextInput
          data-testid="title"
          label="分析タイトル"
          inputSize="50"
          {...form.getInputProps("title")}
          required
        />
        <Button
          data-testid="executeAnalysisButton"
          disabled={isAnalysisRunning || isLoading}
          onClick={handleExecuteAnalysis}
          mt="25px"
        >
          分析を実行する
        </Button>
      </Group>
    </form>
  );
};
